import { TypedFormSectionInjectedProps } from '@fl/cmsch-fe-library';
import { filter, sortBy } from 'lodash/fp';
import React, { FC, Fragment, memo, useMemo } from 'react';
import { Opt, pipe, map } from 'ts-opt';
import { Accordion } from 'api/gen/Accordion';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { AccordionField } from 'app/mating/components/criteria-collapse/accordion-field';
import { pairedNumberRangeCriteriaCodes } from 'app/mating/utils/paired-criteria-codes';
import { Ant } from 'common/ant';
import { Criterias } from '../../types/criterias';
import styles from './styles.sass';
export interface AccordionCollapseProps {
  /** Current accordion metadata. */
  accordion: Accordion;
  /** Criteria definitions available in the accordion. */
  criteriaDefinitions: Array<CriteriaDefinition>;
  /** Resets one criteria field from the accordion. */
  resetCriteriaValue(criteriaName: Opt<string>): void;
}
type Props = TypedFormSectionInjectedProps<{
  criterias: Criterias;
}, 'criterias'> & AccordionCollapseProps;
const disabledPercentagesCowAccordionCode = 'H_FEM_EXTERIOR_BV';
const disabledPercentagesBullAccordionCode = 'H_MAL_EXTERIOR';
const shouldDisableTopAnimals = (accordion: Accordion, criteriaDefinition: CriteriaDefinition): boolean => accordion.bookmark === 'FEMALE' && accordion.code === disabledPercentagesCowAccordionCode || accordion.bookmark === 'MALE' && accordion.code === disabledPercentagesBullAccordionCode || pairedNumberRangeCriteriaCodes.includes(criteriaDefinition.code);
const AccordionCollapseBase: FC<Props> = props => {
  const {
    accordion,
    criteriaDefinitions,
    Fields,
    resetCriteriaValue
  } = props;
  const accordionCriteria = useMemo(() => pipe(criteriaDefinitions, filter((criteriaDef: CriteriaDefinition) => criteriaDef.index.accordionId === accordion.id), sortBy((criteriaDef: CriteriaDefinition) => criteriaDef.index.position)), [criteriaDefinitions, accordion.id]);
  const renderedCriteria = useMemo(() => pipe(accordionCriteria, map((criteriaDef: CriteriaDefinition) => <Ant.Col xs={24} xl={12} key={criteriaDef.id} className={styles.accordionFieldWrapper}>
                    <AccordionField criteriaDefinition={criteriaDef} disableTopAnimals={shouldDisableTopAnimals(accordion, criteriaDef)} resetCriteriaValue={resetCriteriaValue} Fields={Fields} />
                </Ant.Col>)), [accordionCriteria, accordion, resetCriteriaValue, Fields]);
  return <Fragment>
            {renderedCriteria}
        </Fragment>;
};

/**
 * Memoized accordion body that renders criteria fields.
 *
 * @param props Accordion metadata and field reset handler.
 * @returns The rendered accordion content.
 */
export const AccordionCollapse = memo(AccordionCollapseBase);